import AdyenCheckout from '@adyen/adyen-web';
import { payments } from '../../../services';
import store from '../../../store';
import localStore from '../../../lib/localStore/localStore';
import { SavedCard } from '../../../modules/billing/types'
import { Address, Customer } from '../../../modules/customer/types';
import { Cart, Product } from '../../../modules/cart/types';
import { getCart } from '../../../modules/cart';
import { getCustomer } from '../../../modules/customer';
import { getDeliveryAddress } from '../../../modules/delivery';
import { getBillingAddress, selectPaymentMethod, updatePayment } from '../../../modules/billing';
import { getAdyen, getCountryCode } from '../../../modules/config';
import { updateNotification, updateDialog } from '../../../modules/notification';
import { ServiceResponse } from '../../payment-sdk/models';
import PaymentMethod from '../PaymentMethod';
import { AdyenResponseCode, AdyenInitResponse, AdyenAddress, AdyenLineItem } from './types';
import { translate } from '../../../modules/translation';
import "firebase/analytics";
import {local as browserLocalStorage} from "store2";
import newRelicData from '../../../newrelic';
import { getPaymentServiceDetails } from "../paymentHelper";

/**
 * Converts an address from Redux to the Adyen address schema:
 * https://docs.adyen.com/risk-management/automate-submitting-referrals/referrals-api-reference#shopperaddress
 * @param address
 */
export function convertAppAddressToAdyenAddress(address: Address): AdyenAddress {
    const EMPTY_ADDRESS_FIELD_PLACEHOLDER = '-';
    const defaultCountry = getAdyen(store.getState().config).countryCode ?? 'GB';
    const adyenAddress: AdyenAddress = {
        city: address.town || EMPTY_ADDRESS_FIELD_PLACEHOLDER,
        country: address.locale.toUpperCase() || defaultCountry,
        houseNumberOrName: address.address1 || EMPTY_ADDRESS_FIELD_PLACEHOLDER,
        postalCode: address.postcode || EMPTY_ADDRESS_FIELD_PLACEHOLDER,
        street: address.address2 || EMPTY_ADDRESS_FIELD_PLACEHOLDER,
    };

    const countryCode = address.locale.toUpperCase();
    if (countryCode === 'US' || countryCode === 'CA') {
        adyenAddress.stateOrProvince = address.county || EMPTY_ADDRESS_FIELD_PLACEHOLDER;
    }

    return adyenAddress;
}

/**
 * This function should be called after a call to `payments().initPayment('adyen', ...)` and will continue the payment journey depending on the result code.
 * @param paymentModule The payment method module (eg. Card, ApplePay, etc.)
 * @param paymentAPIResponse The response from the initPayment call sent back by Adyen.
 */
export async function processAdyenResponse(paymentModule: PaymentMethod, paymentAPIResponse: ServiceResponse) {
    const { rawResponse: initRawResponse, paymentID } = paymentAPIResponse;
    const initResponse: any = initRawResponse ? JSON.parse(initRawResponse) as AdyenInitResponse : undefined;
    const paymentTransactionID = paymentID ? paymentID : '';
    const resultCode = initResponse?.resultCode ? initResponse.resultCode as AdyenResponseCode : '';
    const { clientKey, provider, methodName, methodId } = getPaymentServiceDetails();

    try {
        newRelicData({ actionName: 'adyenHelper', userFunction: 'processAdyenResponse', message: { resultCode: resultCode, pspRefence: initResponse?.pspReference }});
        switch (resultCode) {
            case 'Authorised':
                const gatewayReference = initResponse?.pspReference ? initResponse.pspReference : '';
                if (!gatewayReference) throw new Error('Authorised - Invalid gatewayReference');
                const paymentResultResponse = await payments().finalisePayment(provider, methodName, gatewayReference, resultCode, paymentTransactionID);
                if (paymentResultResponse.data?.hasFailed === false) {
                    const { ID: orderID, status: resultStatus, invoiceID } = paymentResultResponse.data;
                    const isPaid = (resultStatus === 'PAID' || resultStatus === 'ACCEPTED' ||  resultStatus === 'ACKNOWLEDGED_OK');
                    store.dispatch(updatePayment(String(orderID), String(resultStatus), isPaid, invoiceID));
                    newRelicData({ actionName: 'adyenPaymentAuthorisation', userFunction: 'processAdyenResponse', message: `result status ${resultStatus}`, result: 'success'});
                }
                else {
                    newRelicData({ actionName: 'adyenPaymentAuthorisation', userFunction: 'processAdyenResponse', message: { paymentProvider: provider , error: paymentResultResponse }, paymentName: methodName, result: 'failure' });
                    throw new Error('Payment failed when finalising the payment');
                }
                await paymentModule.onComplete();
                break;

            case 'RedirectShopper':
                newRelicData({ actionName: 'adyenHelper', userFunction: 'processAdyenResponse', message: `action type ${initResponse.action?.type}`});
                if (initResponse.action?.type !== 'redirect') throw new Error(`RedirectShopper - Invalid initResponse action type: ${initResponse.action?.type}`);

                localStore.set('adyenPaymentData', initResponse.action?.paymentData ?? '');
                localStore.set('adyenPaymentID', paymentTransactionID);
                // The Redux reducers' data expected by the Confirmation page need to persist through browser reload.
                localStore.saveStatesToStorage(['delivery', 'cart', 'billing', 'config']);
                const container = (initResponse.action.paymentMethodType === 'clicktopay')  ? '#adyen-action-modal' : '#customCard-container';
                newRelicData({ actionName: 'adyenHelper', userFunction: 'processAdyenResponse', message: `container: ${container} card method: ${initResponse.additionalData.paymentMethod} payment method type: ${initResponse.action.paymentMethodType}`});
                const adyenConfig = getAdyen(store.getState().config);
                const checkout = await AdyenCheckout({
                    locale: adyenConfig.locale,
                    environment: adyenConfig.environment,
                    clientKey: clientKey,
                    onAdditionalDetails: async (state: any, component: any) => {
                        await payments().updatePayment(provider, methodName, state.data, paymentTransactionID, '3DS');
                    }
                });
                checkout.createFromAction(initResponse.action).mount(container);
                break;

            // TODO populate when integrating payment methods that can potentially return these codes
            case 'AuthenticationFinished':
            case 'AuthenticationNotRequired':
            case 'Cancelled':
                if (window.location.pathname.includes('/paymentredirect')) {
                    const translation = store.getState().translation;
                    let message = translate(translation, 'err_adyen_payment_cancelled', "The payment has been cancelled.")
                    store.dispatch(updateDialog('adyen_issue_on_redirection', {content: message}));
                } else {
                    store.dispatch(updateNotification('adyen_payment_cancelled', {
                        code: initResponse.refusalReasonCode,
                        message: initResponse.refusalReason,
                    }));
                }
                browserLocalStorage.remove('state');
                break;
            case 'Pending':
            case 'Received':
                const gatewayRef = initResponse.pspReference ?? '';
                if (!gatewayRef) throw new Error('Authorised - Invalid gatewayReference');
                try {
                    newRelicData({ actionName: 'adyenHelper', userFunction: 'processAdyenResponse', message: 'Pending | Received: Before finalisePayment call'});
                    const paymentResultResponse = await payments().finalisePayment(provider, methodId, gatewayRef, resultCode, paymentTransactionID);
                    if (paymentResultResponse.data?.hasFailed === false) {
                        const { ID: orderID, status: resultStatus, invoiceID  } = paymentResultResponse.data;
                        const isPaid = (resultStatus === 'PAID' || resultStatus === 'ACCEPTED' ||  resultStatus === 'ACKNOWLEDGED_OK');
                        store.dispatch(updatePayment(String(orderID), String(resultStatus), isPaid, invoiceID));
                        newRelicData({ actionName: 'adyenPaymentAuthorisation', userFunction: 'processAdyenResponse', message: resultStatus, result: 'success'});
                    }
                    newRelicData({ actionName: 'adyenHelper', userFunction: 'processAdyenResponse', message: 'Pending | Received: After finalisePayment call'});
                } catch (error){
                    newRelicData({ actionName: 'adyenPaymentAuthorisation', userFunction: 'processAdyenResponse', message: { paymentProvider: 'adyen' , error: (error as Error).message },  paymentName: methodName, result: 'failure' });
                }
                if (window.location.pathname.includes('/paymentredirect')) {
                    const translation = store.getState().translation;
                    let message = translate(translation, 'thereWasAnErrorWhenAttemptingTo', "There was an error when attempting to pay, please try again. No money has been taken and your cart is still intact.")
                    store.dispatch(updateDialog('adyen_issue_on_redirection', {content: message}));
                } else {
                    store.dispatch(updateNotification('adyen_payment_cancelled', {
                        code: initResponse.refusalReasonCode,
                        message: initResponse.refusalReason,
                    }));
                }
                browserLocalStorage.remove('state');
                break;
            case 'ChallengeShopper':
            case 'IdentifyShopper':
            case 'PresentToShopper':
                break;

            case 'Refused':
                const refusalCode = initResponse.refusalReasonCode;
                newRelicData({ actionName: 'adyenPaymentAuthorisation', userFunction: 'processAdyenResponse', message: `refused code ${refusalCode}`, result: 'failure'});
                if (methodId === 'CARD' && refusalCode && !['11', '24'].includes(refusalCode)) {
                     await store.dispatch(selectPaymentMethod(''));
                     await store.dispatch(selectPaymentMethod(methodId));
                }
                
                switch(refusalCode) {
                    case '2': // Refused
                        store.dispatch(updateDialog('adyen_refusal_code_refused'));
                        break;
                    default:
                        if (window.location.pathname.includes('/paymentredirect')) {
                            store.dispatch(updateDialog('adyen_issue_on_redirection', {content: initResponse.refusalReason, title: initResponse.refusalReasonCode}));
                        } else {
                            store.dispatch(updateNotification('adyen_payment_refused', {
                                code: initResponse.refusalReasonCode,
                                message: initResponse.refusalReason,
                            }));
                        }
                        break;
                }
                browserLocalStorage.remove('state');
                break;

            case 'Error':
                if (window.location.pathname.includes('/paymentredirect')) {
                    store.dispatch(updateDialog('adyen_issue_on_redirection', {content: initResponse.refusalReason, title: initResponse.refusalReasonCode}));
                } else {
                    store.dispatch(updateNotification('adyen_error', {
                        code: initResponse.refusalReasonCode,
                        message: initResponse.refusalReason,
                    }));
                }
                newRelicData({ actionName: 'adyenPaymentAuthorisation', userFunction: 'processAdyenResponse', message: `error ${refusalCode}`, result: 'failure'});
                browserLocalStorage.remove('state');
                break;

            default:
                newRelicData({ actionName: 'adyenPaymentAuthorisation', userFunction: 'processAdyenResponse', message: `Unknown init payment response: ${resultCode}`, result: 'failure'});
                throw new Error(`Unknown init payment response: ${resultCode}`);
        }
    }
    catch (error) {
        newRelicData({ actionName: 'adyenPaymentAuthorisation', userFunction: 'processAdyenResponse', message: (error as Error).message, result: 'failure' });
        paymentModule.onError(error);
    }
}

    /**
     * Gets the user's bank cards saved in Adyen.
     * @param cart Used to make the cart object expected by the payment service
     * @param customer Used to make the cart object expected by the payment service
     * @param billingAddress Used to make the cart object expected by the payment service
     * @param deliveryAddress Used to make the cart object expected by the payment service
     */
export async function getUserSavedCards(cart: Cart, customer: Customer, billingAddress: Address, deliveryAddress: Address) {
    try {
        const countryCode = getCountryCode(store.getState().config).toLowerCase();
        const availableMethodsResponse = await payments().getAvailableMethodsForCart('adyen', cart, customer, billingAddress, deliveryAddress, countryCode);

        const savedCards = availableMethodsResponse.data.storedPaymentMethods?.map(method => {
            return {
                id: method.id,
                name: method.name,
                brand: method.brand,
                lastFour: method.lastFour,
                holderName: method.holderName,
                expiryMonth: parseInt(method.expiryMonth),
                expiryYear: parseInt(method.expiryYear),
                isPreferredSavedCard: false
            } as SavedCard;
        });
        return savedCards;
    }
    catch (error) {
        console.error('getUserSavedCards', error);
        newRelicData({ actionName: 'getUserSavedCards', userFunction: 'getUserSavedCards', message: (error as Error).message, result: 'failure'});
        store.dispatch(updateNotification('error_cant_get_saved_adyen_cards'));
        return [] as SavedCard[];
    }
}

/**
 * Runs the function getAvailableMethodsForCart() with Adyen set as the provider.
 * @returns AdyenPaymentMethodsResponse, or undefined if there was an error.
 */
export async function getAvailableAdyenMethodsForCart() {
    const cart = getCart(store.getState().cart);
    const customer = getCustomer(store.getState().customer);
    const billingAddress = getBillingAddress(store.getState().billing);
    const deliveryAddress = getDeliveryAddress(store.getState().delivery);
    const countryCode = getCountryCode(store.getState().config).toLowerCase(); 
    try {
        return await payments().getAvailableMethodsForCart(
            'adyen',
            cart,
            customer,
            billingAddress,
            deliveryAddress,
            countryCode
        );
    }
    catch (error) {
        newRelicData({ actionName: 'getAvailableAdyenMethodsForCart', userFunction: 'getAvailableAdyenMethodsForCart', message:  (error as Error).message, result: 'failure'});
        console.error('getAvailableMethodsForCart error', error);
    }
}

/**
 * This takes an array of Products from this app and converts it to the Adyen product format.
 * @param products Array of app products
 * @returns Array of product objects formatted the Adyen way
 */
export function getAdyenLineItemsFromAppProducts(products: Product[]): AdyenLineItem[] {
    const promotionProducts:AdyenLineItem[] = [];
    const allProducts = products.map(product => {
        //check for promotion group products
        if(product.products){
            for (let promotionProduct of product.products){
                promotionProducts.push(getProductDataforAdyen(promotionProduct));
            }
            return {};
        }
        return getProductDataforAdyen(product);
    });

    let productsForAdyen = [...allProducts, ...promotionProducts];
    return productsForAdyen.filter((productForAdyen) => {
        return !!productForAdyen.description;
    });
}

/**
 * This takes an single Product and converts it to the Adyen product format.
 * @param products Array of app products
 * @returns product object formatted the Adyen way
 */
function getProductDataforAdyen(product:Product):  AdyenLineItem {
    return {
        description: product.name,
        quantity: product.quantity.toString(),
        amountIncludingTax: parseInt((parseFloat(product.subtotal.amount) * 100).toFixed(2)),
        id: product.SKU,
        imageUrl: product.image.originalURL
    }

}